/**
 * Tabs color / intent — the ink axis.
 * Density lives in Tabs.size.ts. Literals stay complete for Tailwind detection.
 *
 * Intents (aligned with Button / Tag):
 * - primary  → purple family
 * - secondary → dark-invirtus (solid brand noir — NOT soft gray)
 * - info     → blue family
 * - success  → green family
 * - muted    → soft gray / background-gray (quiet chrome only)
 *
 * Rule: hover/inactive stay in the same family as active.
 * Icon ink always matches label ink in the same state.
 */

import type { TabsColor } from './Tabs.types';

/** Pill track surface behind horizontal default segments. */
export const TABS_TRACK: Record<TabsColor, string> = {
  primary: 'bg-purple-soft/60',
  secondary: 'bg-dark-invirtus/10',
  info: 'bg-blue-soft/60',
  success: 'bg-green-soft/60',
  muted: 'bg-background-gray',
};

/** Horizontal line — active underline. */
export const TABS_LINE_ACTIVE: Record<TabsColor, string> = {
  primary: 'border-purple text-dark-invirtus font-semibold',
  secondary: 'border-dark-invirtus text-dark-invirtus font-semibold',
  info: 'border-blue text-dark-invirtus font-semibold',
  success: 'border-green text-dark-invirtus font-semibold',
  muted: 'border-dark-invirtus-soft text-dark-invirtus/80 font-semibold',
};

/** Horizontal line — inactive + hover (family-tinted border). */
export const TABS_LINE_INACTIVE: Record<TabsColor, string> = {
  primary:
    'border-transparent text-dark-invirtus/60 hover:text-dark-invirtus hover:border-purple/50',
  secondary:
    'border-transparent text-dark-invirtus/60 hover:text-dark-invirtus hover:border-dark-invirtus/50',
  info:
    'border-transparent text-dark-invirtus/60 hover:text-dark-invirtus hover:border-blue/60',
  success:
    'border-transparent text-dark-invirtus/60 hover:text-dark-invirtus hover:border-green/50',
  muted:
    'border-transparent text-dark-invirtus/50 hover:text-dark-invirtus/70 hover:border-dark-invirtus-soft',
};

/** Horizontal default (pill) — active raised segment. */
export const TABS_PILL_ACTIVE: Record<TabsColor, string> = {
  primary: 'bg-light text-dark-invirtus font-semibold shadow-sm ring-1 ring-purple/20',
  secondary: 'bg-dark-invirtus text-light font-semibold shadow-sm',
  info: 'bg-light text-dark-invirtus font-semibold shadow-sm ring-1 ring-blue/20',
  success: 'bg-light text-dark-invirtus font-semibold shadow-sm ring-1 ring-green/20',
  muted: 'bg-light text-dark-invirtus/80 font-semibold shadow-sm',
};

export const TABS_PILL_INACTIVE: Record<TabsColor, string> = {
  primary: 'text-dark-invirtus/60 hover:text-dark-invirtus hover:bg-purple-soft/40',
  secondary: 'text-dark-invirtus/60 hover:text-dark-invirtus hover:bg-dark-invirtus/10',
  info: 'text-dark-invirtus/60 hover:text-dark-invirtus hover:bg-blue-soft/40',
  success: 'text-dark-invirtus/60 hover:text-dark-invirtus hover:bg-green-soft/50',
  muted: 'text-dark-invirtus/50 hover:text-dark-invirtus/70 hover:bg-background-gray',
};

/** Vertical default — soft/solid fill active. */
export const TABS_VERTICAL_FILL_ACTIVE: Record<TabsColor, string> = {
  primary: 'bg-purple-soft text-dark-invirtus',
  secondary: 'bg-dark-invirtus text-light',
  info: 'bg-blue-soft text-dark-invirtus',
  success: 'bg-green-soft text-dark-invirtus',
  muted: 'bg-background-gray text-dark-invirtus/80',
};

/** Vertical default — inactive + hover stays in the same family as active. */
export const TABS_VERTICAL_FILL_INACTIVE: Record<TabsColor, string> = {
  primary: 'text-dark-invirtus hover:bg-purple-soft/40',
  secondary: 'text-dark-invirtus hover:bg-dark-invirtus/10',
  info: 'text-dark-invirtus hover:bg-blue-soft/40',
  success: 'text-dark-invirtus hover:bg-green-soft/50',
  muted: 'text-dark-invirtus/70 hover:bg-background-gray',
};

/**
 * Vertical line — left accent bar, no fill.
 * Border + ink follow `color`; description stays muted via its own class.
 */
export const TABS_VERTICAL_LINE_ACTIVE: Record<TabsColor, string> = {
  primary: 'border-purple text-purple',
  secondary: 'border-dark-invirtus text-dark-invirtus',
  info: 'border-blue text-blue',
  success: 'border-green text-green',
  muted: 'border-dark-invirtus-soft text-dark-invirtus/80',
};

export const TABS_VERTICAL_LINE_INACTIVE: Record<TabsColor, string> = {
  primary: 'border-transparent text-dark-invirtus/70 hover:bg-purple-soft/35 hover:border-purple/30',
  secondary: 'border-transparent text-dark-invirtus/70 hover:bg-dark-invirtus/10 hover:border-dark-invirtus/40',
  info: 'border-transparent text-dark-invirtus/70 hover:bg-blue-soft/35 hover:border-blue/40',
  success: 'border-transparent text-dark-invirtus/70 hover:bg-green-soft/40 hover:border-green/40',
  muted: 'border-transparent text-dark-invirtus/50 hover:bg-background-gray',
};

/** Focus ring aligned to intent. */
export const TABS_FOCUS: Record<TabsColor, string> = {
  primary: 'focus-visible:ring-purple/40',
  secondary: 'focus-visible:ring-dark-invirtus/40',
  info: 'focus-visible:ring-blue/40',
  success: 'focus-visible:ring-green/40',
  muted: 'focus-visible:ring-dark-invirtus-soft/50',
};

/** Count pill — active / inactive. */
export const TABS_COUNT_ACTIVE: Record<TabsColor, string> = {
  primary: 'bg-purple-soft text-dark-invirtus',
  secondary: 'bg-light/15 text-light',
  info: 'bg-blue-soft text-dark-invirtus',
  success: 'bg-green-soft text-dark-invirtus',
  muted: 'bg-dark-invirtus-soft text-dark-invirtus/70',
};

export const TABS_COUNT_INACTIVE: Record<TabsColor, string> = {
  primary: 'bg-purple-soft/50 text-dark-invirtus/70',
  secondary: 'bg-dark-invirtus/10 text-dark-invirtus/70',
  info: 'bg-blue-soft/50 text-dark-invirtus/70',
  success: 'bg-green-soft/60 text-dark-invirtus/70',
  muted: 'bg-background-gray text-dark-invirtus/50',
};

/**
 * Icon / label ink — MUST match each other in the same state.
 * Secondary fill/pill active uses light ink on dark-invirtus surface.
 */
export const TABS_ICON_LINE_ACTIVE: Record<TabsColor, string> = {
  primary: 'text-purple',
  secondary: 'text-dark-invirtus',
  info: 'text-blue',
  success: 'text-green',
  muted: 'text-dark-invirtus/80',
};

export const TABS_ICON_FILL_ACTIVE: Record<TabsColor, string> = {
  primary: 'text-dark-invirtus',
  secondary: 'text-light',
  info: 'text-dark-invirtus',
  success: 'text-dark-invirtus',
  muted: 'text-dark-invirtus/80',
};

export const TABS_ICON_INACTIVE = 'text-dark-invirtus/60';

export const TABS_LABEL_FILL_ACTIVE: Record<TabsColor, string> = {
  primary: 'font-semibold text-dark-invirtus',
  secondary: 'font-semibold text-light',
  info: 'font-semibold text-dark-invirtus',
  success: 'font-semibold text-dark-invirtus',
  muted: 'font-semibold text-dark-invirtus/80',
};

export const TABS_LABEL_LINE_ACTIVE: Record<TabsColor, string> = {
  primary: 'font-semibold text-purple',
  secondary: 'font-semibold text-dark-invirtus',
  info: 'font-semibold text-blue',
  success: 'font-semibold text-green',
  muted: 'font-semibold text-dark-invirtus/80',
};

export const TABS_LABEL_INACTIVE_VERTICAL = 'font-medium text-dark-invirtus/60';

/** Description — secondary fill needs light muted ink on dark surface. */
export const TABS_DESCRIPTION =
  'block text-xs text-dark-invirtus/50 font-body mt-0.5 truncate';

export const TABS_DESCRIPTION_ON_DARK =
  'block text-xs text-light/60 font-body mt-0.5 truncate';
